package com.solevo.dist.pages.orderdetails

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.solevo.dist.components.DashboardPageLayout
import com.solevo.dist.components.RequireAuth
import com.solevo.dist.components.WaitForAuthAndViewModel
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color.Companion.rgb
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.solevo.dist.components.StatusPill
import com.solevo.dist.i18n.LocalStrings
import com.solevo.dist.theme.styles.tableBodyCell
import com.solevo.dist.theme.styles.tableHeaderCell
import com.solevo.dist.theme.styles.tableHeaderRow
import com.solevo.dist.theme.styles.tableRow
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronLeft
import com.varabyte.kobweb.silk.components.icons.fa.FaDownload
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import kotlinx.browser.window
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import com.solevo.dist.util.Res
import com.solevo.dist.viewmodels.InvoiceDetailsViewModel

@Page("/orderdetails/invoice")
@Composable
fun InvoiceDetailsScreen() {
    RequireAuth { InvoiceDetailsPage() }
}

@Composable
fun InvoiceDetailsPage() {
    val raw = remember { window.localStorage.getItem("invoice_details") ?: "" }
    
    WaitForAuthAndViewModel(
        key = raw,
        viewModelFactory = { InvoiceDetailsViewModel(raw) },
        onViewModelReady = { vm -> vm.start() }
    ) { vm ->
        val ui by vm.state.collectAsState()

        DashboardPageLayout {
            Column(Modifier.fillMaxWidth().padding(24.px)) {
            val strings = LocalStrings.current
            // Title row with back + download button on the right
            val context = rememberPageContext()
            Row(Modifier.fillMaxWidth().alignItems(AlignItems.Center).justifyContent(JustifyContent.SpaceBetween)) {
                Row(Modifier.alignItems(AlignItems.Center)) {
                    FaChevronLeft(
                        modifier = Modifier
                            .margin(top = 4.px)
                            .cursor(Cursor.Pointer)
                            .onClick { context.router.navigateTo("/orders") },
                        size = IconSize.LG
                    )
                    SpanText(
                        "${strings.wordInvoice} ${ui.invoiceNumber}",
                        Modifier.margin(left = 12.px).fontSize(28.px).fontWeight(FontWeight.Bold)
                    )
                }

                if (!ui.docUrl.isNullOrBlank()) {
                    Row(
                        Modifier
                            .alignItems(AlignItems.Center)
                            .backgroundColor(rgb(24, 119, 242))
                            .color(rgb(255, 255, 255))
                            .padding(leftRight = 16.px, topBottom = 10.px)
                            .borderRadius(8.px)
                            .cursor(Cursor.Pointer)
                            .onClick { window.open(ui.docUrl!!, target = "_blank") }
                    ) {
                        FaDownload(modifier = Modifier.color(rgb(255, 255, 255)), size = IconSize.SM)
                        SpanText(strings.download, Modifier.margin(left = 10.px).color(rgb(255, 255, 255)))
                    }
                }
            }

            Box(Modifier.height(16.px))

            // Details + Repayment cards
            Column(Modifier.fillMaxWidth().gap(24.px)) {
                // Details row
                Column(Modifier.fillMaxWidth()) {
                    SpanText(
                        strings.details,
                        Modifier
                            .fontSize(18.px)
                            .fontWeight(FontWeight.Bold)
                            .color(rgb(36, 41, 47))
                    )
                    Box(Modifier.height(8.px))
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .display(DisplayStyle.Grid)
                            .styleModifier {
                                property("grid-template-columns", "repeat(3, 1fr)")
                                property("column-gap", "74px")
                                property("row-gap", "24px")
                                property("align-items", "center")
                                property("justify-items", "stretch")
                                property("box-sizing", "border-box")
                                property("min-width", "0")
                            }
                    ) {
                        DetailRow(
                            modifier = Modifier.fillMaxWidth(),
                            title = strings.headerTotal,
                            value = ui.totalAmountText,
                            iconSrc = Res.Icon.OrderIcons.accountBalance
                        )
                        DetailRow(
                            modifier = Modifier.fillMaxWidth(),
                            title = strings.labelInvoiceId,
                            value = ui.invoiceNumber,
                            iconSrc = Res.Icon.OrderIcons.numbers
                        )
                        DetailRow(
                            modifier = Modifier.fillMaxWidth(),
                            title = strings.labelDateCreated,
                            value = ui.createdText,
                            iconSrc = Res.Icon.OrderIcons.calendar
                        )
                    }
                }

                // Repayment row
                Column(Modifier.fillMaxWidth()){
                    SpanText(
                        strings.repayment,
                        Modifier
                            .fontSize(18.px)
                            .fontWeight(FontWeight.Bold)
                            .color(rgb(36, 41, 47))
                    )
                    Box(Modifier.height(8.px))
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .display(DisplayStyle.Grid)
                            .styleModifier {
                                property("grid-template-columns", "repeat(3, 1fr)")
                                property("column-gap", "74px")
                                property("row-gap", "24px")
                                property("align-items", "center")
                                property("justify-items", "stretch")
                                property("box-sizing", "border-box")
                                property("min-width", "0")
                            }
                    ) {
                        StatusDetailRow(
                            modifier = Modifier.fillMaxWidth(),
                            title = strings.labelStatus,
                            statusLabel = ui.statusLabel,
                            statusColorHex = ui.statusColorHex,
                            iconSrc = Res.Icon.OrderIcons.status
                        )
                        DetailRow(
                            modifier = Modifier.fillMaxWidth(),
                            title = strings.labelPaid,
                            value = ui.paidText,
                            iconSrc = Res.Icon.OrderIcons.accountBalance
                        )
                        DetailRow(
                            modifier = Modifier.fillMaxWidth(),
                            title = strings.labelRemaining,
                            value = ui.remainingText,
                            iconSrc = Res.Icon.OrderIcons.accountBalance
                        )
                        DetailRow(
                            modifier = Modifier.fillMaxWidth(),
                            title = strings.headerDueDate,
                            value = ui.dueDateText,
                            iconSrc = Res.Icon.OrderIcons.calendar
                        )
                    }
                }
            }

            Box(Modifier.height(24.px))

            Row(
                Modifier
                    .fillMaxWidth()
                    .display(DisplayStyle.Grid)
                    .styleModifier { property("grid-template-columns", "1.2fr 0.8fr 1fr 0.6fr 0.8fr") }
                    .tableHeaderRow()
            ) {
                SpanText(strings.headerProduct, Modifier.tableHeaderCell())
                SpanText(strings.headerTotal, Modifier.tableHeaderCell())
                SpanText(strings.headerWarehouse, Modifier.tableHeaderCell())
                SpanText(strings.headerUnits, Modifier.tableHeaderCell())
                SpanText(strings.headerPricePerUnit, Modifier.tableHeaderCell())
            }

            val listContext = rememberPageContext()
            ui.items.forEach { item ->
                Row(
                    Modifier
                        .fillMaxWidth()
                        .display(DisplayStyle.Grid)
                        .styleModifier { property("grid-template-columns", "1.2fr 0.8fr 1fr 0.6fr 0.8fr") }
                        .tableRow()
                        .cursor(Cursor.Pointer)
                        .onClick { listContext.router.navigateTo(com.solevo.dist.navigation.Screen.ProductDetailsPage.withId(item.productId)) }
                ) {
                    SpanText(item.name.ifBlank { "—" }.let { if (it.length > 35) it.take(35) + "…" else it }, Modifier.tableBodyCell())
                    SpanText(item.totalPrice?.let { formatAmount(it) } ?: "—", Modifier.tableBodyCell())
                    SpanText(item.warehouse ?: "-", Modifier.tableBodyCell())
                    SpanText(formatUnits(item.quantity, item.unit), Modifier.tableBodyCell())
                    SpanText(item.pricePerUnit?.let { formatPricePerUnit(it, item.unit) } ?: "—", Modifier.tableBodyCell())
                }
            }
            }
        }
    }
}

@Composable
private fun DetailRow(
    modifier: Modifier = Modifier,
    title: String,
    value: String,
    iconSrc: String,
    valueColor: com.varabyte.kobweb.compose.ui.graphics.Color? = null
) {
    val bgColor: com.varabyte.kobweb.compose.ui.graphics.Color? = null
    Column(
        modifier
            .fillMaxWidth()
    ) {
        Row(
            Modifier
                .fillMaxWidth()
                .alignItems(AlignItems.Center)
                .padding(bottom = 12.px)
                .borderBottom(1.px, LineStyle.Solid, rgb(240, 242, 246))
                .let { if (bgColor != null) it.backgroundColor(bgColor) else it }
                .styleModifier { property("min-height", "48px") }
        ) {
            Box(
                Modifier
                    .width(18.px)
                    .height(18.px)
                    .flexShrink(0)
                    .margin(right = 10.px)
            ) { Image(src = iconSrc, alt = "icon") }
            Column(Modifier.margin(left = 8.px).fillMaxWidth()) {
                SpanText(title, Modifier.color(rgb(120, 130, 140)).styleModifier { property("white-space", "nowrap") })
                Box(Modifier.height(4.px))
                SpanText(value.ifBlank { "—" }, Modifier.then(if (valueColor != null) Modifier.color(valueColor) else Modifier).styleModifier { property("white-space", "nowrap") })
            }
        }
    }
}

@Composable
private fun StatusDetailRow(
    modifier: Modifier = Modifier,
    title: String,
    statusLabel: String,
    statusColorHex: String,
    iconSrc: String
) {
    Column(
        modifier
            .fillMaxWidth()
    ) {
        Row(
            Modifier
                .fillMaxWidth()
                .alignItems(AlignItems.Center)
                .padding(bottom = 12.px)
                .borderBottom(1.px, LineStyle.Solid, rgb(240, 242, 246))
                .styleModifier { property("min-height", "48px") }
        ) {
            Box(
                Modifier
                    .width(18.px)
                    .height(18.px)
                    .flexShrink(0)
                    .margin(right = 10.px, bottom = 2.px)
                    .styleModifier { property("align-self", "center") }
            ) { Image(src = iconSrc, alt = "icon") }
            SpanText(title, Modifier.margin(left = 8.px, right = 16.px).color(rgb(120, 130, 140)).styleModifier { property("white-space", "nowrap"); property("align-self", "center") })
            StatusPill(
                text = statusLabel,
                colorHex = statusColorHex,
                useIconForInvoiceStatuses = false
            )
        }
    }
}


private fun formatAmount(value: Double): String {
    val abs = kotlin.math.abs(value).toLong()
    val s = abs.toString()
    val parts = buildList {
        var i = s.length
        while (i > 3) {
            add(s.substring(i - 3, i))
            i -= 3
        }
        add(s.substring(0, i))
    }.asReversed().joinToString(" ")
    val sign = if (value < 0) "-" else ""
    return sign + parts + " FCFA"
}

private fun formatUnits(qty: Double, unit: String?): String {
    val q = formatAmount(qty)
    return if (unit.isNullOrBlank()) q else q.replace(" FCFA", "") + " " + unit
}

private fun formatPricePerUnit(price: Double, unit: String?): String {
    val p = formatAmount(price)
    return if (unit.isNullOrBlank()) p else "$p / ${unit}"
}


